import React, { useState, useEffect, useMemo } from "react";
import Loading from "../../../common/components/loading/loading";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";
import { getCommodityHistory } from "../../../common/services/products/commodityService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const CommodityHistory = ({ commodityId }) => {
  const { t } = useTranslation(["products"]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchCommodityHistory = async () => {
      if (!commodityId) return;

      try {
        setLoading(true);
        const data = await getCommodityHistory(commodityId);
        setHistory(mapToViewModel(data));
      } catch (error) {
        console.error("Error fetching commodity history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommodityHistory();
  }, [commodityId]);

  // Map the history data to a view model
  const mapToViewModel = (data) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return data.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      commodityClass: item.commodityClassName,
      active: item.active,
      updatedByUserAccountId: item.createdByUserAccountId,
      updatedBy:
        item.updatedByUserAccountFirstName +
        " " +
        item.updatedByUserAccountLastName,
      updatedDate: new Date(item.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(item.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: item.eventId,
    }));
  };

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        sort: "desc",
        filter: true,
      },
      {
        field: "name",
        headerName: t("CommodityName"),
        sortable: true,
        filter: true,
      },
      {
        field: "description",
        headerName: t("CommodityDescription"),
        sortable: true,
        filter: true,
      },
      {
        field: "commodityClass",
        headerName: t("CommodityClass"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  if (loading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("CommodityID")} {commodityId}
      </div>
      <ThemeableAgGrid
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={history}
        getRowId={(params) => params.data.id.toString()}
        domLayout="autoHeight"
        animateRows={true}
      />
    </div>
  );
};

export default CommodityHistory;

